import { useEffect, useRef, useState } from "react";
import Decimal from "decimal.js";
import type { BetPanel } from "@crashx/shared";
import { useRoundStore } from "../../state/roundStore.js";
import { selectCanBet, useConnectionStore } from "../../state/connectionStore.js";
import { AutoBetSession, type AutoSettings, type AutoStop } from "./AutoBetSession.js";
import { validBetSettings } from "./betSettings.js";
import type { BetActions } from "./useBetActions.js";

export function useAutoBet(panel: BetPanel, amount: string, autoCashout: string, place: BetActions["place"]) {
  const session = useRef<AutoBetSession | null>(null);
  const pumpRef = useRef<() => void>(() => {});
  const [status, setStatus] = useState<{
    running: boolean;
    remaining: number;
    reason: AutoStop | null;
    total: number;
    currentRound: number;
    net: string;
  }>({
    running: false,
    remaining: 0,
    reason: null,
    total: 0,
    currentRound: 1,
    net: "0",
  });
  useEffect(() => {
    let mounted = true;
    const publish = (run: AutoBetSession) => {
      if (mounted && session.current === run)
        setStatus({
          running: run.reason === null,
          remaining: run.remaining,
          reason: run.reason,
          total: run.total,
          currentRound: run.currentRound,
          net: run.netResult,
        });
    };
    const pump = () => {
      const run = session.current;
      if (!run || run.reason) return;
      const state = useRoundStore.getState();
      for (const bet of state.myBets) if (run.settle(bet)) publish(run);
      if (run.reason) return;
      if (!selectCanBet(useConnectionStore.getState())) {
        run.stop("disconnected");
        publish(run);
        return;
      }
      const round = state.round;
      if (
        round?.phase !== "BETTING" ||
        state.myBets.some(
          (bet) => bet.panel === panel && (bet.status === "active" || bet.status === "pending"),
        )
      )
        return;
      const currency = state.config?.currencies[0];
      if (
        !currency ||
        !validBetSettings(
          amount,
          autoCashout,
          currency,
          state.balances[currency.code]?.balance ?? null,
          state.config?.maxMultiplier ?? 1,
        )
      ) {
        run.stop("rejected");
        publish(run);
        return;
      }
      if (!run.claim(round.roundId)) return;
      void place(
        new Decimal(amount).toNumber(),
        currency.code,
        autoCashout === "" ? null : Number(autoCashout),
      ).then((bet) => {
        if (bet) run.accept(bet.betId);
        else run.stop("rejected");
        if (mounted) {
          publish(run);
          pump();
        }
      });
    };
    pumpRef.current = pump;
    const unsubscribeRound = useRoundStore.subscribe(pump);
    const unsubscribeConnection = useConnectionStore.subscribe(pump);
    return () => {
      mounted = false;
      session.current?.stop("stopped");
      unsubscribeRound();
      unsubscribeConnection();
    };
  }, [panel, amount, autoCashout, place]);
  return {
    ...status,
    start: (settings: AutoSettings) => {
      const run = new AutoBetSession(settings);
      session.current = run;
      setStatus({
        running: true,
        remaining: run.remaining,
        reason: null,
        total: run.total,
        currentRound: 1,
        net: "0",
      });
      pumpRef.current();
    },
    stop: () => {
      if (!session.current) return;
      session.current?.stop("stopped");
      setStatus((current) => ({ ...current, running: false, reason: "stopped" }));
    },
  };
}
